import { mergeInto, millisecondsPer, peek, toDate, toMilliseconds } from '@art-suite/art-core-ts';
import { ProgressUpdate, WorkStatus } from './ScanRoot/ProgressUpdateSchema';
import { ScanPhoto } from './ScanRoot/ScanRootSchemas';
import { ScanPartOptimized } from './ScanRootOptimized';

const PROGRESS_TIME_WINDOW = millisecondsPer.hour * 8;

// ********************************************************************************
// Exported Types
// ********************************************************************************

/**
 * A day's worth of progress data for presentation purposes.
 * Combines a progress update's status fields with all photos and notes from that day.
 */
export type FullStatusDetails = ProgressUpdate & { photos: ScanPhoto[]; groupingDateString: string };

// ********************************************************************************
// Private Helper Functions
// ********************************************************************************
export const getTaskProgressGroupingDateString = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const isWithinProgressTimeWindow = (date1: Date, date2: Date) => {
  return Math.abs(toMilliseconds(date1) - toMilliseconds(date2)) <= PROGRESS_TIME_WINDOW;
};

const getProgressUpdateForDate = (scanPart: ScanPartOptimized, date: Date): ProgressUpdate =>
  scanPart.progressUpdates?.find(update => update.updatedAt && isWithinProgressTimeWindow(update.updatedAt, date))
    ?? { updatedAt: date };

// ********************************************************************************
// Exported Public Functions
// ********************************************************************************

/**
 * Finds the most recent progress update for a scan part by scanning all updates.
 * Does not assume the array is sorted - performs an O(n) scan to find the maximum updatedAt.
 * @param scanPart - The scan part to get the latest progress update from
 * @returns The progress update with the most recent updatedAt, or undefined if none exist
 */
export const getLatestProgressUpdate = (scanPart: ScanPartOptimized | undefined): ProgressUpdate | undefined => {
  const updates = scanPart?.progressUpdates;
  if (!updates || updates.length === 0) return undefined;
  let latest = updates[0];
  for (let i = 1; i < updates.length; i++) {
    if (toMilliseconds(updates[i].updatedAt) > toMilliseconds(latest.updatedAt)) latest = updates[i];
  }
  return latest;
};

/**
 * Gets the work status from the most recent progress update for a scan part.
 * @param scanPart - The scan part to get the latest status from
 * @returns The status of the most recent progress update, or undefined if none exist
 */
export const getLatestProgressStatus = (scanPart: ScanPartOptimized | undefined): WorkStatus | undefined => getLatestProgressUpdate(scanPart)?.status;

/**
 * Creates an updated progressUpdates array by merging a new progress update into a scan part.
 *
 * Uses a time window to determine if updates should be merged:
 * - If an existing update is within the window of the new update's timestamp, they are merged (new values override)
 * - If no existing update is within the window, the new update is appended
 *
 * The returned array is sorted chronologically by updatedAt.
 *
 * @param scanPart - The scan part containing existing progress updates
 * @param progressUpdate - The new progress update to merge in
 * @returns An object with a `progressUpdates` array suitable for updating the scan part
 */
export const getScanPartUpdatesForProgressUpdate = (scanPart: ScanPartOptimized, progressUpdate: ProgressUpdate) => {
  const existingProgressUpdate = getProgressUpdateForDate(scanPart, progressUpdate.updatedAt);
  const progressUpdates: ProgressUpdate[] = [];
  if (scanPart.progressUpdates) {
    for (const update of scanPart.progressUpdates) {
      if (update !== existingProgressUpdate) {
        progressUpdates.push(update);
      }
    }
  }
  progressUpdates.push({ ...existingProgressUpdate, ...progressUpdate });
  progressUpdates.sort((a, b) => toMilliseconds(a.updatedAt) - toMilliseconds(b.updatedAt));
  return { progressUpdates };
};

/**
 * UX Helper: Groups progress updates and photos by local calendar day for presenting a daily progress history.
 *
 * Clusters data by calendar day for display in a daily progress log UI.
 * If multiple progress updates occur on the same calendar day, only the most recent status is shown.
 * All photos from that day are collected regardless.
 *
 * Photos with missing timestamps can't be placed on a real day, but dropping them from the
 * progress walk is worse than showing them at the wrong date — users rely on this view to see
 * progress photos. So they're collected into a single bucket dated one day before the earliest
 * dated entry, which keeps them visible at the very start (oldest slot) of the history.
 * (TODO: remove this fallback once all photos reliably carry a timestamp.)
 *
 * @param progressUpdates - Array of progress updates to cluster
 * @param photos - Array of photos to cluster
 * @param now - The current date (defaults to new Date(), injectable for testing)
 * @returns Array of FullStatusDetails sorted chronologically by day, suitable for rendering as a list
 */
export const getFullStatusDetailsHistory = (progressUpdates: ProgressUpdate[], photos: ScanPhoto[], now: Date = new Date()): FullStatusDetails[] => {
  const progressUpdatesByDay: Record<string, FullStatusDetails> = {};

  const getFullStatusDetailsForDay = (day: number | Date) => {
    const date = toDate(day);
    const localDateString = getTaskProgressGroupingDateString(date);
    if (!progressUpdatesByDay[localDateString]) {
      progressUpdatesByDay[localDateString] = { updatedAt: date, photos: [], groupingDateString: localDateString };
    }
    return progressUpdatesByDay[localDateString];
  };

  for (const progressUpdate of progressUpdates) {
    const fullStatusDetails = getFullStatusDetailsForDay(progressUpdate.updatedAt);
    const { updatedAt } = progressUpdate;
    const existingUpdatedAt = fullStatusDetails.updatedAt;
    if (updatedAt > existingUpdatedAt) {
      mergeInto(fullStatusDetails, progressUpdate);
    } else {
      mergeInto(fullStatusDetails, progressUpdate, fullStatusDetails);
    }
  }

  const undatedPhotos: ScanPhoto[] = [];
  for (const photo of photos) {
    if (!photo.addedAt) {
      undatedPhotos.push(photo);
      continue;
    }
    const day = getFullStatusDetailsForDay(photo.addedAt);
    day.photos.push(photo);
  }

  // Park date-less photos one day before the earliest dated entry (or before `now` if there are
  // none) so they stay visible at the oldest slot of the history. See the doc above.
  if (undatedPhotos.length > 0) {
    const datedEntries = Object.values(progressUpdatesByDay);
    const earliestMs = datedEntries.length > 0
      ? Math.min(...datedEntries.map(entry => toMilliseconds(entry.updatedAt)))
      : now.getTime();
    const undatedBucket = getFullStatusDetailsForDay(earliestMs - millisecondsPer.day);
    for (const photo of undatedPhotos) undatedBucket.photos.push(photo);
  }

  return Object.values(progressUpdatesByDay).sort((a, b) => toMilliseconds(a.updatedAt) - toMilliseconds(b.updatedAt));
};

/**
 * UX Helper: Extracts progress history from a scan part for presenting a daily progress log.
 *
 * Convenience wrapper for getFullStatusDetailsHistory that pulls data directly from a scan part.
 *
 * @param scanPart - The scan part to extract progress updates and photos from
 * @param now - The current date (defaults to new Date(), injectable for testing)
 * @returns Array of FullStatusDetails sorted chronologically by day, suitable for rendering as a list
 */
export const getFullStatusDetailsHistoryFromScan = (scanPart: ScanPartOptimized, now: Date = new Date()): FullStatusDetails[] =>
  getFullStatusDetailsHistory(scanPart.progressUpdates ?? [], scanPart.photos ?? [], now);

/**
 * UX Helper: Ensures the last entry in the history is always the current editable entry for today.
 *
 * Guarantees that the last element of the returned array represents today's entry.
 * The UX can render all entries as read-only history except the last one, which should be editable.
 *
 * If the last entry is already from today (same local calendar day), returns the array unchanged.
 * Otherwise, appends a new blank entry with `updatedAt: now`.
 *
 * @param sortedFullStatusDetails - The history array (must be sorted chronologically)
 * @param now - The current date (defaults to new Date(), injectable for testing)
 * @returns Array where the last entry is guaranteed to be today's editable entry
 */
export const getFullStatusDetailsHistoryWithLastEntryEditable = (sortedFullStatusDetails: FullStatusDetails[], now: Date = new Date()): FullStatusDetails[] => {
  const lastEntry = peek(sortedFullStatusDetails);
  if (lastEntry && getTaskProgressGroupingDateString(toDate(lastEntry.updatedAt)) === getTaskProgressGroupingDateString(now)) return sortedFullStatusDetails;
  return [...sortedFullStatusDetails, { updatedAt: now, photos: [], groupingDateString: getTaskProgressGroupingDateString(now) }];
};
